import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useReporte, reporteActions } from '../../context/ReporteContext';
import { Button } from '../ui';
import ReferenciasService, { type Faena, type Predio, type Rodal, type FaenaCalificadorCatalog } from '../../services/referencias/referenciasService';
import { type CalificadorFaena } from '../../context/ReporteContext';
import { KmlDropzone } from '../kml/KmlDropzone';
import { KmlList } from '../kml/KmlList';
import { KmlService, type UploadedKml } from '../../services/kml/kmlService';
import { MapaPreview } from '../mapas/MapaPreview';
import famsService from '../../services/famsService';

// ===============================
// INTERFACE DEL MODAL
// ===============================

interface ModalAgregarFaenaProps {
  isOpen: boolean;
  onClose: () => void;
  faenaParaEditar?: any; // Nueva prop para edición
  modoEdicion?: boolean; // Nueva prop para modo
}

interface FaenaFormData {
  faena_id: number | null;
  predio_id: number | null;
  rodal_id: number | null;
  horas: number;
  area_trabajada: number;
  calificadores: CalificadorFaena[];
}

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const ModalAgregarFaena: React.FC<ModalAgregarFaenaProps> = ({ 
  isOpen, 
  onClose,
  faenaParaEditar,
  modoEdicion = false
}) => {
  const { dispatch, tieneEmpresaActiva } = useReporte();
  const { t } = useTranslation();
  
  // Estados del formulario
  const [faenaData, setFaenaData] = useState<FaenaFormData>({
    faena_id: null,
    predio_id: null,
    rodal_id: null,
    horas: 0,
    area_trabajada: 0,
    calificadores: [],
  });

  const [calificadoresCatalogo, setCalificadoresCatalogo] = useState<FaenaCalificadorCatalog[]>([]);

  // Estados KML
  const [archivosKml, setArchivosKml] = useState<UploadedKml[]>([]);

  // Estados locales para inputs numéricos (permite coma/punto decimal)
  const [localHoras, setLocalHoras] = useState(faenaData.horas.toString());
  const [localArea, setLocalArea] = useState(faenaData.area_trabajada.toString());

  // Estados de referencias
  const [faenas, setFaenas] = useState<Faena[]>([]);
  const [predios, setPredios] = useState<Predio[]>([]);
  const [rodales, setRodales] = useState<Rodal[]>([]);
  
  // Estados de carga
  const [cargandoFaenas, setCargandoFaenas] = useState(false);
  const [cargandoPredios, setCargandoPredios] = useState(false);
  const [cargandoRodales, setCargandoRodales] = useState(false);
  const [enviando, setEnviando] = useState(false);

  // Estados FAMS
  const [mostrarInputFams, setMostrarInputFams] = useState(false);
  const [urlFams, setUrlFams] = useState('');
  const [cargandoFams, setCargandoFams] = useState(false);

  // ===============================
  // CARGAR REFERENCIAS
  // ===============================

  const cargarFaenas = async () => {
    if (!tieneEmpresaActiva) return;
    
    try {
      setCargandoFaenas(true);

      const faenasData = await ReferenciasService.obtenerFaenas();

      const faenasFiltradas = ReferenciasService.filtrarActivos(faenasData);

      setFaenas(faenasFiltradas);

    } catch (error) {
      console.error('Error al cargar faenas:', error);
      toast.error(t('faenas.loading_faenas'));
    } finally {
      setCargandoFaenas(false);
    }
  };

  const cargarPredios = async () => {
    if (!tieneEmpresaActiva) return;
    
    try {
      setCargandoPredios(true);

      const prediosData = await ReferenciasService.obtenerPredios();

      const prediosFiltrados = ReferenciasService.filtrarActivos(prediosData);

      setPredios(prediosFiltrados);

    } catch (error) {
      console.error('Error al cargar predios:', error);
      toast.error(t('faenas.loading_predios'));
    } finally {
      setCargandoPredios(false);
    }
  };

  const cargarRodales = async (predioId?: number) => {
    try {
      setCargandoRodales(true);
      const rodalesData = await ReferenciasService.obtenerRodales(predioId!);
      setRodales(ReferenciasService.filtrarActivos(rodalesData));

    } catch (error) {
      console.error('Error al cargar rodales:', error);
      toast.error(t('faenas.loading_rodales'));
    } finally {
      setCargandoRodales(false);
    }
  };

  // ===============================
  // EFFECTS
  // ===============================

  // Sincronizar estados locales cuando cambia faenaData
  useEffect(() => {
    setLocalHoras(faenaData.horas.toString());
    setLocalArea(faenaData.area_trabajada.toString());
  }, [faenaData.horas, faenaData.area_trabajada]);

  // Cargar faenas, predios, rodales y calificadores al abrir modal
  useEffect(() => {
    if (isOpen && tieneEmpresaActiva) {
      cargarFaenas();
      cargarPredios();
      cargarRodales();
      ReferenciasService.obtenerCalificadores().then(setCalificadoresCatalogo);
    }
  }, [isOpen, tieneEmpresaActiva]);

  // 🆕 NUEVO: Inicializar con datos de faena si es edición
  useEffect(() => {
    if (modoEdicion && faenaParaEditar && isOpen) {
      setFaenaData({
        faena_id: faenaParaEditar.faena_id,
        predio_id: faenaParaEditar.predio_id,
        rodal_id: faenaParaEditar.rodal_id,
        horas: faenaParaEditar.horas,
        area_trabajada: faenaParaEditar.area_trabajada,
        calificadores: faenaParaEditar.calificadores || [],
      });
      
      // ✅ TRANSFORMAR archivos KML del backend al formato frontend
      const transformarArchivosKml = async () => {
        if (!faenaParaEditar.archivos_kml || faenaParaEditar.archivos_kml.length === 0) {
          setArchivosKml([]);
          return;
        }
        
        try {
          const archivosTransformados = await Promise.all(
            faenaParaEditar.archivos_kml.map(async (archivoBackend: any) => {
              try {
                // Verificar si el archivo tiene un ID válido del backend (< 1000000000000)
                // Los IDs reales son secuenciales (1, 2, 561, etc.)
                // Los IDs temporales usan Date.now() (> 1700000000000)
                const archivoId = archivoBackend.archivo_id || archivoBackend.id;
                const esIdValido = archivoId && archivoId < 1000000000000;

                let poligonos: any[] = [];
                let lineas: any[] = [];

                // Solo cargar desde backend si tiene ID válido
                if (esIdValido) {
                  // Cargar polígonos y líneas del archivo desde el backend
                  const geometrias = await KmlService.getGeometrias(archivoId);

                  const geometriasConCoordenadas = geometrias.map(geometria => ({
                    ...geometria,
                    coordinates: KmlService.convertirWKTACoordenadas(geometria.geometria)
                  }));

                  // Separar polígonos y líneas
                  poligonos = geometriasConCoordenadas.filter((g: any) =>
                    g.tipo_geometria === 'POLYGON' ||
                    (g.area_hectareas !== undefined && g.area_hectareas !== null && g.area_hectareas > 0)
                  );

                  lineas = geometriasConCoordenadas.filter((g: any) =>
                    g.tipo_geometria === 'LINESTRING' ||
                    (g.longitud_metros !== undefined && g.longitud_metros !== null && g.longitud_metros > 0)
                  );
                } else {
                  // ID temporal: Usar geometrías que ya están en memoria
                  poligonos = archivoBackend.poligonos || [];
                  lineas = archivoBackend.lineas || [];
                  // console.log(`📝 Usando geometrías en memoria para KML temporal: ${archivoBackend.nombre_original}`);
                }

                // ✅ CALCULAR valores reales basados en las geometrías obtenidas
                const cantidadPoligonosReal = poligonos.length;
                const cantidadLineasReal = lineas.length;
                const longitudTotalReal = lineas.reduce((total: number, linea: any) => {
                  return total + (linea.longitud_metros || 0);
                }, 0);

                // ✅ NUEVO: Calcular área total desde polígonos si no viene del backend
                const areaTotalPoligonos = poligonos.reduce((total: number, p: any) => {
                  return total + (p.area_hectareas || 0);
                }, 0);

                const todasLasGeometrias = [...poligonos, ...lineas];

                // Transformar al formato UploadedKml que espera el frontend
                const archivoFrontend: UploadedKml = {
                  archivo_id: archivoId,
                  nombre_original: archivoBackend.nombre_original,
                  estado: 'completado',
                  // ✅ PRIORIDAD: Usar área del backend si existe, sino calcular desde polígonos
                  area_hectareas: archivoBackend.area_hectareas || areaTotalPoligonos,
                  cantidad_poligonos: cantidadPoligonosReal, // ✅ USAR valor calculado
                  longitud_metros: longitudTotalReal, // ✅ USAR valor calculado
                  cantidad_lineas: cantidadLineasReal, // ✅ USAR valor calculado
                  poligonos: poligonos,
                  lineas: lineas, // ✅ Campo separado para líneas
                  // Incluir el file original si está disponible (para KMLs temporales)
                  file: archivoBackend.file || undefined,
                  // Calcular bounds a partir de todas las geometrías
                  bounds: todasLasGeometrias.length > 0 ? KmlService.calcularBounds(todasLasGeometrias) : undefined,
                  orden_en_faena: 1
                };

                return archivoFrontend;
              } catch (error) {
                console.error(`Error cargando geometrías para archivo ${archivoBackend.nombre_original}:`, error);
                // Retornar archivo sin geometrías si falla
                const archivoId = archivoBackend.archivo_id || archivoBackend.id;
                return {
                  archivo_id: archivoId,
                  nombre_original: archivoBackend.nombre_original,
                  estado: 'completado',
                  area_hectareas: archivoBackend.area_hectareas,
                  cantidad_poligonos: 0, // Sin geometrías
                  longitud_metros: 0, // Sin líneas
                  cantidad_lineas: 0, // Sin líneas
                  poligonos: [],
                  lineas: [], // ✅ Campo separado vacío
                  file: archivoBackend.file || undefined, // Mantener file si existe
                  orden_en_faena: 1
                } as UploadedKml;
              }
            })
          );
          
          setArchivosKml(archivosTransformados);
          
        } catch (error) {
          console.error('Error transformando archivos KML:', error);
          toast.error('Error cargando geometrías de los archivos KML');
          setArchivosKml([]);
        }
      };
      
      transformarArchivosKml();
    }
  }, [modoEdicion, faenaParaEditar, isOpen]);

  // Cargar rodales cuando cambia el predio
  useEffect(() => {
    if (faenaData.predio_id) {
      cargarRodales(faenaData.predio_id);
      // Limpiar rodal seleccionado al cambiar predio
      setFaenaData(prev => ({ ...prev, rodal_id: null }));
    } else {
      // 🔄 Si no hay predio, cargar todos los rodales
      cargarRodales();
    }
  }, [faenaData.predio_id]);

  // ===============================
  // HANDLERS
  // ===============================

  const handleInputChange = (field: keyof FaenaFormData, value: any) => {
    setFaenaData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCalificadorChange = (calificadorId: number, valor: string) => {
    setFaenaData(prev => {
      const resto = prev.calificadores.filter(c => c.calificador_id !== calificadorId);
      if (valor === '') return { ...prev, calificadores: resto };
      return { ...prev, calificadores: [...resto, { calificador_id: calificadorId, valor }] };
    });
  };

  // ===============================
  // HANDLERS KML
  // ===============================

  const handleKmlUploaded = (kml: UploadedKml) => {
    const nuevosKml = [...archivosKml, kml];
    setArchivosKml(nuevosKml);
  };

  // Handler para importar KML desde FAMS
  const handleImportarFams = async () => {
    if (!urlFams.trim()) {
      toast.error(t('faenas.fams_enter_link'));
      return;
    }

    if (!famsService.isValidFamsUrl(urlFams)) {
      toast.error(t('faenas.fams_invalid'));
      return;
    }

    try {
      setCargandoFams(true);

      // Obtener información del KML
      const famsData = await famsService.convertToKmlInfo(urlFams);

      // Convertir base64 a File
      const filename = `${famsData.nombre}.kml`;
      const file = famsService.base64ToFile(famsData.kml_base64, filename);

      // Procesar el archivo KML usando el servicio existente
      const nuevoKml = await KmlService.calcularAreaReal(file);
      nuevoKml.orden_en_faena = archivosKml.length + 1;

      // Agregar a la lista
      setArchivosKml(prev => [...prev, nuevoKml]);

      // Limpiar y cerrar
      setUrlFams('');
      setMostrarInputFams(false);
      toast.success(`KML importado: ${famsData.nombre} (${famsData.area_hectareas} ha)`);

    } catch (error: any) {
      console.error('Error importando FAMS:', error);
      toast.error(error.response?.data?.detail || 'Error al importar enlace FAMS');
    } finally {
      setCargandoFams(false);
    }
  };

  const tituloModal = modoEdicion ? t('faenas.edit_faena') : t('faenas.add_faena');
  const textoBoton = modoEdicion ? t('faenas.update_faena') : t('faenas.add_faena');

  const handleEliminarKml = async (_archivo: UploadedKml, index: number) => {
    try {
      // En desarrollo, eliminar directamente
      // En checkpoint 5: await KmlService.delete(archivo.archivo_id);
      
      const nuevosKml = archivosKml.filter((_, i) => i !== index);
      setArchivosKml(nuevosKml);
      

      
    } catch (error) {
      console.error('Error eliminando archivo KML:', error);
      toast.error('Error al eliminar archivo KML');
    }
  };

  const handleAgregar = async () => {
    // Validaciones
    if (!faenaData.faena_id) {
      toast.error(t('faenas.faena_required'));
      return;
    }

    if (faenaData.horas <= 0) {
      toast.error(t('faenas.hours_required'));
      return;
    }

    try {
      setEnviando(true);
      
      // Buscar información de las referencias seleccionadas
      const faenaSeleccionada = ReferenciasService.buscarPorId(faenas, faenaData.faena_id);
      const predioSeleccionado = faenaData.predio_id ? ReferenciasService.buscarPorId(predios, faenaData.predio_id) : null;
      const rodalSeleccionado = faenaData.rodal_id ? ReferenciasService.buscarPorId(rodales, faenaData.rodal_id) : null;

      if (modoEdicion && faenaParaEditar) {
        // 🆕 MODO EDICIÓN: Actualizar faena existente
        const faenaActualizada = {
          ...faenaParaEditar,
          faena_id: faenaData.faena_id,
          predio_id: faenaData.predio_id,
          rodal_id: faenaData.rodal_id,
          horas: faenaData.horas,
          area_trabajada: faenaData.area_trabajada,
          calificadores: faenaData.calificadores,

          descripcion: faenaSeleccionada?.descripcion || `Faena ${faenaData.faena_id}`,
          faena_codigo: faenaSeleccionada?.codigo || '',
          predio_codigo: predioSeleccionado?.codigo || 'Sin predio',
          rodal_codigo: rodalSeleccionado?.codigo || 'Sin rodal',

          archivos_kml: archivosKml,
          area_kml_total: KmlService.calcularAreaTotal(archivosKml),
          longitud_kml_total: KmlService.calcularLongitudTotal(archivosKml),
        };

        dispatch(reporteActions.actualizarFaena(faenaActualizada));
        toast.success(`Faena ${faenaSeleccionada?.codigo} actualizada exitosamente`);
        
      } else {
        // MODO AGREGAR: Crear nueva faena (código existente)
        const nuevaFaena = {
          id_temporal: `faena_${Date.now()}`,
          faena_id: faenaData.faena_id,
          predio_id: faenaData.predio_id,
          rodal_id: faenaData.rodal_id,
          horas: faenaData.horas,
          area_trabajada: faenaData.area_trabajada,
          calificadores: faenaData.calificadores,

          descripcion: faenaSeleccionada?.descripcion || `Faena ${faenaData.faena_id}`,
          faena_codigo: faenaSeleccionada?.codigo || '',
          predio_codigo: predioSeleccionado?.codigo || 'Sin predio',
          rodal_codigo: rodalSeleccionado?.codigo || 'Sin rodal',

          archivos_kml: archivosKml,
          area_kml_total: KmlService.calcularAreaTotal(archivosKml),
          longitud_kml_total: KmlService.calcularLongitudTotal(archivosKml),

          unidad_medida: 'ha',
          observaciones: '',
        };

        // Agregar al contexto
        dispatch(reporteActions.agregarFaena(nuevaFaena));
        toast.success(`Faena ${faenaSeleccionada?.codigo} agregada exitosamente`);
      }
      
      // Cerrar modal y resetear
      handleCerrar();
      
    } catch (error) {
      console.error('Error al procesar faena:', error);
      toast.error('Error al procesar faena');
    } finally {
      setEnviando(false);
    }
  };

  const handleCerrar = () => {
    // Resetear formulario
    setFaenaData({
      faena_id: null,
      predio_id: null,
      rodal_id: null,
      horas: 0,
      area_trabajada: 0,
      calificadores: [],
    });
    
    // Limpiar listas
    setRodales([]);
    setArchivosKml([]);
    
    onClose();
  };

  // ===============================
  // VALIDACIONES
  // ===============================

  const esFormularioValido = 
    faenaData.faena_id &&
    // faenaData.predio_id && // 🔄 Opcional
    // faenaData.rodal_id && // 🔄 Opcional
    faenaData.horas > 0;
    // && faenaData.area_trabajada > 0; // 🔄 Área opcional

  // ===============================
  // RENDER
  // ===============================

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {tituloModal}
          </h3>
          <button
            onClick={handleCerrar}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          
          {/* Validación empresa */}
          {!tieneEmpresaActiva && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">
                ⚠️ {t('faenas.no_active_company')}
              </p>
            </div>
          )}

          {/* Formulario */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Faena */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('faenas.faena')} *
              </label>
              <select
                value={faenaData.faena_id || ''}
                onChange={(e) => handleInputChange('faena_id', parseInt(e.target.value) || null)}
                disabled={cargandoFaenas || !tieneEmpresaActiva}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">
                  {cargandoFaenas ? t('faenas.loading_faenas') : t('faenas.select_faena')}
                </option>
                {faenas.map((faena) => (
                  <option key={faena.id} value={faena.id}>
                    {faena.codigo} - {faena.descripcion}
                  </option>
                ))}
              </select>
              {faenas.length === 0 && !cargandoFaenas && tieneEmpresaActiva && (
                <p className="text-xs text-gray-500 mt-1">{t('faenas.no_faenas')}</p>
              )}
            </div>

            {/* Predio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('faenas.predio')} ({t('faenas.optional')})
              </label>
              <select
                value={faenaData.predio_id || ''}
                onChange={(e) => handleInputChange('predio_id', parseInt(e.target.value) || null)}
                disabled={cargandoPredios || !tieneEmpresaActiva}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">
                  {cargandoPredios ? t('faenas.loading_predios') : t('faenas.select_predio')}
                </option>
                {predios.map((predio) => (
                  <option key={predio.id} value={predio.id}>
                    {predio.codigo} - {predio.descripcion}
                  </option>
                ))}
              </select>
              {predios.length === 0 && !cargandoPredios && tieneEmpresaActiva && (
                <p className="text-xs text-gray-500 mt-1">{t('faenas.no_predios')}</p>
              )}
            </div>

            {/* Rodal */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('faenas.rodal')} ({t('faenas.optional')})
              </label>
              <select
                value={faenaData.rodal_id || ''}
                onChange={(e) => handleInputChange('rodal_id', parseInt(e.target.value) || null)}
                disabled={cargandoRodales || !faenaData.predio_id}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">
                  {cargandoRodales ? t('faenas.loading_rodales') :
                   !faenaData.predio_id ? t('faenas.select_predio_first') :
                   t('faenas.select_rodal')}
                </option>
                {rodales.map((rodal) => (
                  <option key={rodal.id} value={rodal.id}>
                    {rodal.codigo} - {rodal.descripcion}
                  </option>
                ))}
              </select>
              {faenaData.predio_id && rodales.length === 0 && !cargandoRodales && (
                <p className="text-xs text-gray-500 mt-1">{t('faenas.no_rodals')}</p>
              )}
            </div>

            {/* Horas Trabajadas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('faenas.worked_hours')} *
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={localHoras}
                onChange={(e) => {
                  const stringValue = e.target.value;
                  setLocalHoras(stringValue);
                  const normalized = stringValue.replace(',', '.');
                  const num = parseFloat(normalized);
                  if (!isNaN(num) && num >= 0 && num <= 24) {
                    handleInputChange('horas', num);
                  } else if (stringValue === '' || stringValue === '0') {
                    handleInputChange('horas', 0);
                  }
                }}
                onBlur={() => {
                  const normalized = localHoras.replace(',', '.');
                  const num = parseFloat(normalized);
                  if (num > 24) {
                    setLocalHoras('24');
                    handleInputChange('horas', 24);
                  } else {
                    setLocalHoras(isNaN(num) ? '0' : num.toString());
                  }
                }}
                pattern="[0-9]*[.,]?[0-9]*"
                placeholder="0.0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">{t('faenas.max_hours')}</p>
            </div>

            {/* Área Trabajada */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('faenas.worked_area')} ({t('faenas.optional')})
              </label>
              <div className="flex space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={localArea}
                    onChange={(e) => {
                      const stringValue = e.target.value;
                      setLocalArea(stringValue);
                      const normalized = stringValue.replace(',', '.');
                      const num = parseFloat(normalized);
                      if (!isNaN(num) && num >= 0) {
                        handleInputChange('area_trabajada', num);
                      } else if (stringValue === '' || stringValue === '0') {
                        handleInputChange('area_trabajada', 0);
                      }
                    }}
                    onBlur={() => {
                      const normalized = localArea.replace(',', '.');
                      const num = parseFloat(normalized);
                      setLocalArea(isNaN(num) ? '0' : num.toString());
                    }}
                    pattern="[0-9]*[.,]?[0-9]*"
                    placeholder="0.00"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  {t('kml.hectares')}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('faenas.area_note')}
              </p>
            </div>
          </div>

          {/* ===============================
             SECCIÓN KML MÚLTIPLE
             =============================== */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900">{t('faenas.kml_files')}</h4>

              {/* Botón importar desde FAMS */}
              <button
                type="button"
                onClick={() => setMostrarInputFams(!mostrarInputFams)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                title="Importar desde FAMS"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                FAMS
              </button>
            </div>

            {/* Input para enlace FAMS */}
            {mostrarInputFams && (
              <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <label className="block text-sm font-medium text-green-800 mb-2">
                  {t('faenas.fams_link')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={urlFams}
                    onChange={(e) => setUrlFams(e.target.value)}
                    placeholder="https://share.fams.app/v3/..."
                    className="flex-1 border border-green-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={cargandoFams}
                  />
                  <button
                    type="button"
                    onClick={handleImportarFams}
                    disabled={cargandoFams || !urlFams.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                  >
                    {cargandoFams ? '...' : 'Importar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMostrarInputFams(false);
                      setUrlFams('');
                    }}
                    className="px-3 py-2 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  {t('faenas.fams_example')}
                </p>
              </div>
            )}

            {/* Dropzone para subir KMLs */}
            <KmlDropzone
              onKmlUploaded={handleKmlUploaded}
              maxFiles={5}
              disabled={!tieneEmpresaActiva || enviando}
              currentFiles={archivosKml}
            />

            {/* Lista de KMLs subidos */}
            {archivosKml.length > 0 && (
              <div className="mt-6">
                <KmlList
                  archivos={archivosKml}
                  onEliminar={handleEliminarKml}
                  readonly={false}
                />
              </div>
            )}

            {/* 🗺️ CHECKPOINT 4: Preview de Mapas */}
            {archivosKml.length > 0 && (
              <div className="mt-6">
                <MapaPreview 
                  kmls={archivosKml}
                  height="350px"
                  showToggle={true}
                  className=""
                  onPolygonClick={(poligono, kmlIndex) => {
                    const kml = archivosKml[kmlIndex];
                    toast(`📍 ${poligono.nombre}\n📁 Archivo: ${kml.nombre_original}\n🏞️ Área: ${poligono.area_hectareas} ha`, {
                      duration: 4000,
                      icon: '🗺️'
                    });
                  }}
                />
              </div>
            )}

          </div>

          {/* ===============================
             CALIFICADORES OPCIONALES
             =============================== */}
          {calificadoresCatalogo.length > 0 && (
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">{t('faenas.qualifiers')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {calificadoresCatalogo.map(cal => {
                  const seleccionado = faenaData.calificadores.find(c => c.calificador_id === cal.id)?.valor || '';
                  return (
                    <div key={cal.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {cal.descripcion}
                      </label>
                      <select
                        value={seleccionado}
                        onChange={e => handleCalificadorChange(cal.id, e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">{t('faenas.no_qualifier')}</option>
                        {cal.opciones.map(op => (
                          <option key={op} value={op}>{op}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {esFormularioValido ? (
              <span className="text-green-600">✅ {t('faenas.form_complete')}</span>
            ) : (
              <span className="text-yellow-600">⚠️ {t('faenas.form_incomplete')}</span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleCerrar}
              disabled={enviando}
            >
              {t('common.cancel')}
            </Button>

            <Button
              onClick={handleAgregar}
              disabled={!esFormularioValido || enviando || !tieneEmpresaActiva}
              className={`${
                esFormularioValido && tieneEmpresaActiva
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-400 cursor-not-allowed text-white'
              }`}
            >
              {enviando ? t('common.processing') : textoBoton}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalAgregarFaena;